import { Command, dayMonthDateString, Shift, Store } from '@nextforce/api'
import { Divider, makeStyles, Text } from '@rneui/themed'
import { onlineManager } from '@tanstack/react-query'
import { font } from 'app/theme'
import React, { useCallback, useState } from 'react'
import { View } from 'react-native'
import { Card } from 'shared/components/card'
import { CommandButtons } from 'shared/components/command_buttons'
import { ShiftAttributesView } from 'shared/components/shift/attributes'
import { ShiftCompensationMessageView } from 'shared/components/shift/compensation_message'
import { ShiftPayView } from 'shared/components/shift/pay'
import { ShiftPositionView } from 'shared/components/shift/position'
import { ShiftStoreView } from 'shared/components/shift/store'
import { StoreRatingView } from 'shared/components/shift/store_rating'
import { ShiftTimeRangeDurationView } from 'shared/components/shift/time_range_duration'
import { openMap } from 'shared/utils'

interface Props {
  shift: Shift
  onError?: (error: any) => void
  performOpenShiftAction(command: Command): Promise<Shift | undefined>
}

export const OPEN_SHIFT_CARD_HEIGHT = 300
export const OPEN_SHIFT_TOP_MARGIN = 15

export function OpenShiftCard({ shift, performOpenShiftAction }: Props) {
  const styles = useStyles()

  const [shiftDetails, setShiftDetails] = useState<Shift>(shift)
  const ratingLifetime = shift.store?.storeRatingLifetime
  const onButtonPress = useCallback(
    async (command: Command) => {
      try {
        const shiftData = await performOpenShiftAction(command)
        if (shiftData) {
          setShiftDetails(shiftData)
        }
      } catch (error) {
        console.error(error)
      }
    },
    [performOpenShiftAction]
  )

  return (
    <Card style={styles.container}>
      <View style={styles.card}>
        <ShiftPositionView position={shiftDetails.position} />
        <View style={styles.payAttributes}>
          <ShiftPayView
            amount={shiftDetails.amount}
            shiftAmountExplanationMessage={
              shiftDetails.shiftAmountExplanationMessage
            }
          />
          <View style={styles.attributesContainer}>
            <ShiftAttributesView shift={shiftDetails} />
          </View>
        </View>
        <ShiftCompensationMessageView shift={shiftDetails} />
        <Divider style={styles.divider} />
        <Text style={styles.date}>{dayMonthDateString(shiftDetails.date)}</Text>
        <ShiftTimeRangeDurationView shift={shiftDetails} />
        <OpenShiftNoteView shift={shift} />
        {ratingLifetime?.rating ? (
          <View>
            <StoreRatingView ratingLifetime={ratingLifetime} />
          </View>
        ) : null}
        <Divider style={styles.divider} />
        <ShiftStoreView
          shift={shiftDetails}
          showApprover
          onStorePress={onStorePress}
        />
        <CommandButtons
          disabled={!onlineManager.isOnline()}
          commands={shiftDetails.commands}
          onButtonPress={onButtonPress}
        />
      </View>
    </Card>
  )
}

const onStorePress = (store: Store) => {
  const address = `${store.address},${store.city},${store.zipCode}`
  openMap(address, store.name).catch(e => console.log(e))
}

interface OpenShiftNoteViewProps {
  shift: Shift
}

function OpenShiftNoteView({ shift }: OpenShiftNoteViewProps) {
  const styles = useStyles()
  if (!shift?.note) {
    return null
  }

  return <Text style={styles.text}>{shift.note}</Text>
}

const useStyles = makeStyles(theme => ({
  container: {
    marginTop: OPEN_SHIFT_TOP_MARGIN,
    height: OPEN_SHIFT_CARD_HEIGHT,
  },
  card: {
    paddingHorizontal: 15,
    paddingVertical: 10,
    flex: 1,
    justifyContent: 'space-between',
  },
  divider: {
    backgroundColor: theme.colors?.grey4,
  },
  date: {
    color: theme.colors?.black,
    fontFamily: font.bold,
    fontSize: 15,
  },
  payAttributes: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  attributesContainer: {
    marginLeft: 8,
  },
  text: {
    color: theme.colors?.primary,
  },
}))
